import React, { useEffect, useMemo, useState } from 'react';
import { fmtDateTime, todayIso, syncDateRange } from '../utils/dateUtils';
import { viewKotBillById } from '../utils/kotPrint';
import KotBillToCompany from './KotBillToCompany';

function KotBillReports({ token, selectedProperty, selectedOutletId = '', allowedOutletIds = [], canCorrectPaymentMode = false }) {
  const detectMobileUi = () => {
    if (typeof window === 'undefined') return false;
    const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(window.navigator?.userAgent || '');
    return (coarsePointer || uaMobile) && window.innerWidth <= 1024;
  };
  const [isMobile, setIsMobile] = useState(() => detectMobileUi());
  const today = todayIso();
  const [outlets, setOutlets] = useState([]);
  const [scope, setScope] = useState({
    chain_id: selectedProperty?.chain_id || '',
    property_id: selectedProperty?.id || '',
    outlet_id: ''
  });
  const [filters, setFilters] = useState({ status: '', order_type: '', settlement_mode: '', payment_mode: '', date_from: today, date_to: today, limit: 300 });
  const [settlement, setSettlement] = useState({
    settlement_mode: 'DIRECT_PAYMENT',
    payment_mode: 'CASH',
    reference_no: '',
    room_number: '',
    guest_name: '',
    mobile_last4: '',
    manager_approved: false
  });
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [lifecycleRows, setLifecycleRows] = useState([]);
  const [kotFlowRows, setKotFlowRows] = useState([]);
  const [stationPerfRows, setStationPerfRows] = useState([]);
  const [tableUtilRows, setTableUtilRows] = useState([]);
  const [salesMixRows, setSalesMixRows] = useState([]);
  const [itemDemandRows, setItemDemandRows] = useState([]);
  const [demandForecastRows, setDemandForecastRows] = useState([]);
  const [cancelRows, setCancelRows] = useState([]);
  const [settlementSummary, setSettlementSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('bills');
  const [loading, setLoading] = useState(false);
  const [settlingId, setSettlingId] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [refundingBill, setRefundingBill] = useState(null);
  const [refundForm, setRefundForm] = useState({ refund_amount: '', refund_mode: 'CASH', refund_reason: '' });
  const [refundLoading, setRefundLoading] = useState(false);
  // Correct Payment Mode (Hotel Admin) + per-bill correction history
  const [correctingBill, setCorrectingBill] = useState(null);
  const [correctForm, setCorrectForm] = useState({ payment_id: '', new_mode: 'UPI', reference_no: '', reason: '', customer_gstin: '', customer_name_bill: '', customer_address: '' });
  const [correctLoading, setCorrectLoading] = useState(false);
  const [historyBill, setHistoryBill] = useState(null);
  const [historyRows, setHistoryRows] = useState(null); // null while loading
  const [historyGstRows, setHistoryGstRows] = useState([]);
  // Correct Mode also carries the customer's GST details (company / GSTIN / address)
  const [gstLookupMsg, setGstLookupMsg] = useState('');
  const [correctError, setCorrectError] = useState('');
  // Correct Mode → Bill to Company: company list + inline "new company"
  const [cmCompanies, setCmCompanies] = useState(null);
  const [cmNewOpen, setCmNewOpen] = useState(false);
  const [cmNew, setCmNew] = useState({ corporate_name: '', gst_number: '', billing_address: '', credit_limit: '' });
  const [cmNewBusy, setCmNewBusy] = useState(false);
  const [cmNewMsg, setCmNewMsg] = useState('');
  const tapMin = { minHeight: '44px' };

  useEffect(() => {
    const onResize = () => setIsMobile(detectMobileUi());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const chainId = String(scope.chain_id || '').trim();
  const propertyId = String(scope.property_id || '').trim();
  const outletId = String(scope.outlet_id || '').trim();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId, 'x-outlet-id': outletId }),
    [token, chainId, propertyId, outletId]
  );

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const buildQuery = () => {
    const q = new URLSearchParams();
    if (filters.status) q.set('status', filters.status);
    if (filters.order_type) q.set('order_type', filters.order_type);
    if (filters.settlement_mode) q.set('settlement_mode', filters.settlement_mode);
    if (filters.payment_mode) q.set('payment_mode', filters.payment_mode);
    if (filters.date_from) q.set('date_from', filters.date_from);
    if (filters.date_to) q.set('date_to', filters.date_to);
    if (filters.limit) q.set('limit', String(filters.limit));
    return q.toString();
  };

  const loadOutlets = async (nextChainId, nextPropertyId) => {
    if (!nextChainId || !nextPropertyId) {
      setOutlets([]);
      setScope((prev) => ({ ...prev, outlet_id: '' }));
      return;
    }
    try {
      const res = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(nextPropertyId)}`, {
        headers: { Authorization: `Bearer ${token}`, 'x-chain-id': nextChainId, 'x-property-id': nextPropertyId }
      });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || 'Failed to load outlets');
      const allowSet = new Set((Array.isArray(allowedOutletIds) ? allowedOutletIds : []).map((v) => String(v)));
      const list = (Array.isArray(data) ? data : []).filter((o) => allowSet.size === 0 || allowSet.has(String(o.id)));
      setOutlets(list);
      const preferred = list.find((o) => String(o.id) === String(selectedOutletId));
      setScope((prev) => ({
        ...prev,
        outlet_id: list.length ? String(preferred?.id || (list.find((o) => String(o.id) === String(prev.outlet_id)) || list[0]).id) : ''
      }));
    } catch (err) {
      setError(err?.message || 'Failed to load outlets');
      setOutlets([]);
      setScope((prev) => ({ ...prev, outlet_id: '' }));
    }
  };

  const loadReport = async () => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    if (!outletId) {
      setError('Select outlet first');
      return;
    }
    setLoading(true);
    setError('');
    const query = buildQuery();
    try {
      const [summaryRes, detailRes, settlementRes, slotRes] = await Promise.all([
        fetch(`/api/v1/kot/reports/bills/summary?${query}`, { headers }),
        fetch(`/api/v1/kot/reports/bills/detail?${query}`, { headers }),
        fetch(`/api/v1/kot/reports/bills/settlement-summary?${query}`, { headers }),
        fetch('/api/v1/kot/masters/meal-periods/effective', { headers }).catch(() => null)
      ]);
      if (slotRes && slotRes.ok) {
        const slotParsed = await parseJson(slotRes);
        // parseJson wraps the body as { data, error }.
        setTimeSlots(Array.isArray(slotParsed?.data) ? slotParsed.data : []);
      }

      const summaryParsed    = await parseJson(summaryRes);
      const detailParsed     = await parseJson(detailRes);
      const settlementParsed = await parseJson(settlementRes);

      if (!summaryRes.ok)    throw new Error(summaryParsed.error    || 'Failed to load bill summary');
      if (!detailRes.ok)     throw new Error(detailParsed.error     || 'Failed to load bill details');

      setSummary(summaryParsed.data || {});
      setRows(Array.isArray(detailParsed.data) ? detailParsed.data : []);
      if (settlementRes.ok) setSettlementSummary(settlementParsed.data || null);
    } catch (err) {
      setError(err?.message || 'Network error while loading KOT bill report');
    } finally {
      setLoading(false);
    }
  };

  const loadOperationalReports = async () => {
    if (!chainId || !propertyId || !outletId) return;
    const query = buildQuery();
    try {
      const [
        lifecycleRes,
        flowRes,
        stationRes,
        tableRes,
        mixRes,
        demandRes,
        forecastRes,
        cancelRes
      ] = await Promise.all([
        fetch(`/api/v1/kot/reports/orders/lifecycle?${query}`, { headers }),
        fetch(`/api/v1/kot/reports/kots/flow?${query}`, { headers }),
        fetch(`/api/v1/kot/reports/stations/performance?${query}`, { headers }),
        fetch(`/api/v1/kot/reports/tables/utilization?${query}`, { headers }),
        fetch(`/api/v1/kot/reports/orders/sales-mix?${query}`, { headers }),
        fetch(`/api/v1/kot/reports/items/demand?${query}`, { headers }),
        // Forward-looking — not date-range filtered like the others (lookback
        // is server-computed relative to today, forecast window is next 7 days).
        fetch(`/api/v1/kot/reports/items/demand-forecast?limit=25`, { headers }),
        fetch(`/api/v1/kot/reports/orders/cancellations?${query}`, { headers })
      ]);
      const [
        lifecycleParsed,
        flowParsed,
        stationParsed,
        tableParsed,
        mixParsed,
        demandParsed,
        forecastParsed,
        cancelParsed
      ] = await Promise.all([
        parseJson(lifecycleRes),
        parseJson(flowRes),
        parseJson(stationRes),
        parseJson(tableRes),
        parseJson(mixRes),
        parseJson(demandRes),
        parseJson(forecastRes),
        parseJson(cancelRes)
      ]);
      if (!lifecycleRes.ok) throw new Error(lifecycleParsed.error || 'Failed to load order lifecycle report');
      if (!flowRes.ok) throw new Error(flowParsed.error || 'Failed to load KOT flow report');
      if (!stationRes.ok) throw new Error(stationParsed.error || 'Failed to load station performance report');
      if (!tableRes.ok) throw new Error(tableParsed.error || 'Failed to load table utilization report');
      if (!mixRes.ok) throw new Error(mixParsed.error || 'Failed to load sales mix report');
      if (!demandRes.ok) throw new Error(demandParsed.error || 'Failed to load item demand report');
      if (!forecastRes.ok) throw new Error(forecastParsed.error || 'Failed to load item demand forecast');
      if (!cancelRes.ok) throw new Error(cancelParsed.error || 'Failed to load cancellation report');

      setLifecycleRows(Array.isArray(lifecycleParsed.data) ? lifecycleParsed.data : []);
      setKotFlowRows(Array.isArray(flowParsed.data) ? flowParsed.data : []);
      setStationPerfRows(Array.isArray(stationParsed.data) ? stationParsed.data : []);
      setTableUtilRows(Array.isArray(tableParsed.data) ? tableParsed.data : []);
      setSalesMixRows(Array.isArray(mixParsed.data) ? mixParsed.data : []);
      setItemDemandRows(Array.isArray(demandParsed.data) ? demandParsed.data : []);
      // Flatten { items: [{ item_name, ..., forecast: [{date, ...}] }] } into
      // one row per (item, forecast day) so it fits the flat ReportTable shape.
      const forecastItems = Array.isArray(forecastParsed.data?.items) ? forecastParsed.data.items : [];
      const flatForecastRows = forecastItems.flatMap((item) =>
        (item.forecast || []).map((f) => ({
          menu_item_id: item.menu_item_id,
          item_name: item.item_name,
          category_name: item.category_name,
          trend: item.trend,
          confidence: item.confidence,
          date: f.date,
          day_of_week: f.day_of_week,
          predicted_qty: f.predicted_qty,
        }))
      );
      setDemandForecastRows(flatForecastRows);
      setCancelRows(Array.isArray(cancelParsed.data) ? cancelParsed.data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load operational reports');
    }
  };

  useEffect(() => {
    if (selectedProperty?.chain_id && selectedProperty?.id) {
      const nextChainId = String(selectedProperty.chain_id);
      const nextPropertyId = String(selectedProperty.id);
      setScope((prev) => ({ ...prev, chain_id: nextChainId, property_id: nextPropertyId, outlet_id: String(selectedOutletId || '') }));
    } else {
      setScope({ chain_id: '', property_id: '', outlet_id: '' });
      setOutlets([]);
      setRows([]);
      setSummary(null);
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (!selectedOutletId) return;
    setScope((prev) => ({ ...prev, outlet_id: String(selectedOutletId) }));
  }, [selectedOutletId]);

  useEffect(() => {
    if (chainId && propertyId) loadOutlets(chainId, propertyId);
  }, [chainId, propertyId, token]);

  useEffect(() => {
    if (!chainId || !propertyId || !outletId) return;
    loadReport();
    loadOperationalReports();
  }, [token, chainId, propertyId, outletId]);

  useEffect(() => {
    if (!chainId || !propertyId || !outletId) return;
    loadReport();
  }, [filters.status, filters.order_type, filters.settlement_mode, filters.payment_mode]);

  const lookupInhouseGuest = async () => {
    const roomNo = String(settlement.room_number || '').trim();
    if (!roomNo) {
      setError('Enter room number first for lookup');
      return;
    }
    try {
      const res = await fetch(`/api/v1/kot/inhouse-guests?room_number=${encodeURIComponent(roomNo)}`, { headers });
      const parsed = await parseJson(res);
      if (!res.ok) throw new Error(parsed.error || 'Failed to lookup in-house guest');
      const row = Array.isArray(parsed.data) ? parsed.data[0] : null;
      if (!row) {
        setError('No checked-in guest found for this room');
        return;
      }
      setSettlement((prev) => ({
        ...prev,
        guest_name: row.primary_guest_name || prev.guest_name,
        mobile_last4: row.mobile_last4 || prev.mobile_last4
      }));
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to lookup in-house guest');
    }
  };

  const settleBill = async (row) => {
    if (!row?.id || row.status !== 'OPEN') return;
    const confirmed = window.confirm(`Settle Bill #${row.bill_no || row.id}?`);
    if (!confirmed) return;
    setError('');
    setSettlingId(String(row.id));
    try {
      const isDirect = String(settlement.settlement_mode || 'DIRECT_PAYMENT').toUpperCase() === 'DIRECT_PAYMENT';
      if (isDirect) {
        const due = Number(row.due_amount || 0);
        if (due > 0) {
          const payRes = await fetch(`/api/v1/kot/bills/${row.id}/payments`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              payment_mode: settlement.payment_mode || 'CASH',
              amount: due,
              reference_no: settlement.reference_no ? settlement.reference_no : null
            })
          });
          const payParsed = await parseJson(payRes);
          if (!payRes.ok) throw new Error(payParsed.error || 'Failed to add payment');
        }
      } else {
        if (!String(settlement.room_number || '').trim()) throw new Error('Room number is required for Pay at Checkout');
        if (!String(settlement.guest_name || '').trim()) throw new Error('Guest name is required for Pay at Checkout');
        if (String(settlement.mobile_last4 || '').replace(/\D/g, '').length !== 4) throw new Error('Mobile last 4 digits are required');
      }

      const settleRes = await fetch(`/api/v1/kot/bills/${row.id}/settle`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settlement_mode: isDirect ? 'DIRECT_PAYMENT' : 'PAY_AT_CHECKOUT',
          room_number: settlement.room_number ? String(settlement.room_number).trim() : null,
          guest_name: settlement.guest_name ? String(settlement.guest_name).trim() : null,
          mobile_last4: settlement.mobile_last4 ? String(settlement.mobile_last4).replace(/\D/g, '').slice(-4) : null,
          manager_approved: settlement.manager_approved === true
        })
      });
      const settleParsed = await parseJson(settleRes);
      if (!settleRes.ok) throw new Error(settleParsed.error || 'Failed to settle bill');

      await loadReport();
      setToast(`Bill #${row.bill_no || row.id} settled successfully`);
      window.setTimeout(() => setToast(''), 2500);
    } catch (err) {
      setError(err?.message || 'Failed to settle bill');
    } finally {
      setSettlingId('');
    }
  };

  const CORRECT_MODES = ['CASH', 'CARD', 'UPI', 'BANK', 'BILL_TO_COMPANY'];
  const modeLabel = (m) => String(m || '').replace(/_/g, ' ');
  // Payment-mode part of Correct Mode: direct-payment bills with a payment line.
  const canCorrectBill = (r) => canCorrectPaymentMode
    && r.status === 'SETTLED'
    && String(r.settlement_mode || 'DIRECT_PAYMENT').toUpperCase() === 'DIRECT_PAYMENT'
    && !r.nc_reason
    && Array.isArray(r.payment_lines) && r.payment_lines.length > 0
    // A Bill to Company bill can move back to a tender only while the company has paid nothing on it.
    && !(r.btc_corporate_id && Number(r.btc_paid_amount || 0) > 0.004);
  // The Correct Mode button itself: every settled bill, because the customer's
  // GST details can be added to any of them (room charge and NC included).
  const GSTIN_RX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
  const canOpenCorrect = (r) => canCorrectPaymentMode && r.status === 'SETTLED';

  const openCorrectMode = (r) => {
    const first = (r.payment_lines || [])[0] || {};
    const current = String(first.payment_mode || '').toUpperCase();
    setCorrectingBill(r);
    setCorrectError('');
    setGstLookupMsg('');
    setCorrectForm({
      payment_id: String(first.id || ''),
      new_mode: current || 'CASH', // unchanged until the user picks another mode
      reference_no: String(first.reference_no || ''),
      reason: '',
      customer_gstin: String(r.customer_gstin || ''),
      customer_name_bill: String(r.customer_name_bill || ''),
      customer_address: String(r.customer_address || ''),
      btc_corporate_id: '',
      btc_guest: '',
      btc_ref: '',
    });
    setCmNewOpen(false);
    setCmNewMsg('');
  };

  const loadCmCompanies = async () => {
    try {
      const res = await fetch('/api/v1/kot/companies', { headers });
      const parsed = await parseJson(res);
      if (!res.ok) throw new Error(parsed.error || 'Failed to load companies');
      setCmCompanies(Array.isArray(parsed.data) ? parsed.data : []);
    } catch (err) {
      setCmCompanies([]);
      setCorrectError(err?.message || 'Failed to load companies');
    }
  };
  const onCorrectModeChange = (mode) => {
    setCorrectForm((f) => ({ ...f, new_mode: mode }));
    if (mode === 'BILL_TO_COMPANY' && cmCompanies === null) loadCmCompanies();
  };
  // Picking the company fills the GST details from it (still editable).
  const pickCmCompany = (id, list = cmCompanies) => {
    const c = (list || []).find((x) => String(x.corporate_id) === String(id));
    setCorrectForm((f) => ({
      ...f,
      btc_corporate_id: String(id || ''),
      ...(c ? { customer_gstin: String(c.gst_number || ''), customer_name_bill: String(c.corporate_name || ''), customer_address: String(c.billing_address || '') } : {}),
    }));
  };
  const saveCmCompany = async () => {
    const n = cmNew;
    const g = n.gst_number.trim().toUpperCase();
    if (!n.corporate_name.trim()) { setCmNewMsg('Enter the company name'); return; }
    if (g && !GSTIN_RX.test(g)) { setCmNewMsg('Enter a valid 15-character GSTIN or leave it blank'); return; }
    setCmNewBusy(true);
    setCmNewMsg('');
    try {
      const res = await fetch('/api/v1/kot/companies', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...n, gst_number: g, credit_limit: n.credit_limit === '' ? 0 : Number(n.credit_limit) }),
      });
      const parsed = await parseJson(res);
      if (res.status === 409 && parsed.data?.corporate_id) {
        const list = cmCompanies || [];
        pickCmCompany(parsed.data.corporate_id, list);
        setCmNewOpen(false);
        setCmNewMsg(parsed.error || 'Company already exists — selected it');
        return;
      }
      if (!res.ok) throw new Error(parsed.error || 'Failed to save company');
      const list = [...(cmCompanies || []), parsed.data].sort((a, b) => String(a.corporate_name).localeCompare(String(b.corporate_name)));
      setCmCompanies(list);
      pickCmCompany(parsed.data.corporate_id, list);
      setCmNewOpen(false);
      setCmNew({ corporate_name: '', gst_number: '', billing_address: '', credit_limit: '' });
    } catch (err) {
      setCmNewMsg(err?.message || 'Failed to save company');
    } finally {
      setCmNewBusy(false);
    }
  };

  const onGstinChange = async (raw) => {
    const g = String(raw || '').toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15);
    setCorrectForm((f) => ({ ...f, customer_gstin: g }));
    setGstLookupMsg('');
    if (!GSTIN_RX.test(g)) return;
    try {
      const res = await fetch(`/api/v1/kot/gst-customers/${g}`, { headers });
      const parsed = await parseJson(res);
      const found = res.ok ? parsed.data : null;
      if (found?.company_name) {
        setCorrectForm((f) => (f.customer_gstin === g && !f.customer_name_bill.trim() ? { ...f, customer_name_bill: found.company_name } : f));
        setGstLookupMsg(`Company found in earlier bills: ${found.company_name}`);
      }
    } catch { /* lookup is a convenience only */ }
  };

  const doCorrectMode = async () => {
    if (!correctingBill) return;
    const bill = correctingBill;
    const f = correctForm;
    // What changed?
    const modeAllowed = canCorrectBill(bill);
    const line = (bill.payment_lines || []).find((l) => String(l.id) === String(f.payment_id)) || null;
    const ref = String(f.reference_no || '').trim();
    const modeChanged = modeAllowed && !!line
      && (f.new_mode !== String(line.payment_mode || '').toUpperCase()
        || (f.new_mode !== 'BILL_TO_COMPANY' && ref !== String(line.reference_no || '')));
    const g = String(f.customer_gstin || '').trim();
    const name = String(f.customer_name_bill || '').trim();
    const addr = String(f.customer_address || '').trim();
    const toBtc = modeChanged && f.new_mode === 'BILL_TO_COMPANY';
    // Moving to a company without a GSTIN: the server puts the company's name on
    // the bill itself, so the GST-details step is skipped.
    const gstChanged = (g !== String(bill.customer_gstin || '') || name !== String(bill.customer_name_bill || '') || addr !== String(bill.customer_address || ''))
      && !(toBtc && !g);
    const gstClear = gstChanged && !g && !name && !addr;
    if (!modeChanged && !gstChanged) { setCorrectError('Nothing changed. Pick a new mode or enter the GST details.'); return; }
    if (modeChanged && (f.new_mode === 'CARD' || f.new_mode === 'UPI') && !ref) { setCorrectError(`Reference No is required for ${f.new_mode}`); return; }
    if (toBtc && (bill.payment_lines || []).length !== 1) { setCorrectError('Only a bill paid with a single payment can be moved to Bill to Company'); return; }
    if (toBtc && !f.btc_corporate_id) { setCorrectError('Select the company to bill'); return; }
    if (toBtc && !String(f.btc_guest || '').trim()) { setCorrectError('Enter the guest / employee name'); return; }
    if (gstChanged && !gstClear) {
      if (!GSTIN_RX.test(g)) { setCorrectError('Enter a valid 15-character GSTIN'); return; }
      if (!name) { setCorrectError('Company name is required with the GSTIN'); return; }
    }
    if (String(f.reason || '').trim().length < 3) { setCorrectError('Reason for the correction is required'); return; }
    setCorrectLoading(true);
    setCorrectError('');
    const done = [];
    try {
      if (modeChanged) {
        const res = await fetch(`/api/v1/kot/bills/${bill.id}/payments/${line.id}/correct-mode`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            new_mode: f.new_mode, reference_no: toBtc ? '' : ref, reason: f.reason.trim(),
            ...(toBtc ? { bill_to_company: { corporate_id: Number(f.btc_corporate_id), guest_name: f.btc_guest.trim(), reference_no: String(f.btc_ref || '').trim() } } : {}),
          })
        });
        const parsed = await parseJson(res);
        if (!res.ok) throw new Error(parsed.error || 'Failed to correct payment mode');
        done.push(`${parsed.data?.old_mode || ''} → ${parsed.data?.new_mode || ''}`);
      }
      if (gstChanged) {
        const res = await fetch(`/api/v1/kot/bills/${bill.id}/gst-details`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(gstClear
            ? { clear: true, reason: f.reason.trim() }
            : { customer_gstin: g, customer_name_bill: name, customer_address: addr, reason: f.reason.trim() })
        });
        const parsed = await parseJson(res);
        if (!res.ok) throw new Error(`${done.length ? 'Payment mode saved, but ' : ''}${parsed.error || 'Failed to update GST details'}`);
        done.push(gstClear ? 'GST details removed' : `GST bill to ${name}`);
      }
      setCorrectingBill(null);
      await loadReport();
      setToast(`Bill #${bill.bill_no}: ${done.join(' · ')} saved`);
      window.setTimeout(() => setToast(''), 3000);
      if (gstChanged && !gstClear) {
        try { await viewKotBillById(bill.id, headers); } catch (err) { setError(err?.message || 'Saved, but the bill could not be opened for printing'); }
      }
    } catch (err) {
      if (done.length) await loadReport();
      setCorrectError(err?.message || 'Failed to save the correction');
    } finally {
      setCorrectLoading(false);
    }
  };

  const openHistory = async (r) => {
    setHistoryBill(r);
    setHistoryRows(null);
    setHistoryGstRows([]);
    try {
      const [res, gstRes] = await Promise.all([
        fetch(`/api/v1/kot/bills/${r.id}/payment-mode-history`, { headers }),
        fetch(`/api/v1/kot/bills/${r.id}/gst-history`, { headers }),
      ]);
      const parsed = await parseJson(res);
      if (!res.ok) throw new Error(parsed.error || 'Failed to load history');
      const gstParsed = await parseJson(gstRes);
      setHistoryGstRows(gstRes.ok && Array.isArray(gstParsed.data) ? gstParsed.data : []);
      setHistoryRows(Array.isArray(parsed.data) ? parsed.data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load correction history');
      setHistoryBill(null);
    }
  };

  const doRefund = async () => {
    if (!refundingBill) return;
    const amount = Number(refundForm.refund_amount);
    if (!amount || amount <= 0) { alert('Enter a valid refund amount'); return; }
    if (!refundForm.refund_mode) { alert('Select a refund mode'); return; }
    if (!String(refundForm.refund_reason || '').trim()) { alert('Refund reason is required'); return; }
    setRefundLoading(true);
    try {
      const res = await fetch(`/api/v1/kot/bills/${refundingBill.id}/refund`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refund_amount: amount, refund_mode: refundForm.refund_mode, refund_reason: refundForm.refund_reason })
      });
      const parsed = await parseJson(res);
      if (!res.ok) throw new Error(parsed.error || 'Failed to record refund');
      setRefundingBill(null);
      setRefundForm({ refund_amount: '', refund_mode: 'CASH', refund_reason: '' });
      await loadReport();
      setToast(`Refund of ₹${amount.toFixed(2)} recorded for Bill #${refundingBill.bill_no}`);
      window.setTimeout(() => setToast(''), 3000);
    } catch (err) {
      alert(err?.message || 'Failed to record refund');
    } finally {
      setRefundLoading(false);
    }
  };

  // ── Design system for this screen: cards, tables, badges, tabs — consistent
  // with the rest of the app's newer screens (theme-var aware, light/dark safe).
  const ACCENT = '#0d8f6f';
  const card = { background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 14, padding: isMobile ? '1rem' : '1.25rem', marginBottom: '1rem', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' };
  const sectionHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.9rem' };
  const sectionTitle = { margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--theme-text, #1f2937)' };
  const sectionSub = { margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--theme-text-muted, #64748b)' };
  const input = { ...tapMin, padding: '0.55rem 0.7rem', border: '1px solid var(--theme-border, #cbd5e0)', borderRadius: 8, fontSize: isMobile ? '16px' : '14px', background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #1f2937)' };
  const btn = (kind = 'primary') => ({
    ...tapMin, padding: '0.55rem 0.9rem', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: isMobile ? '15px' : '13.5px',
    border: kind === 'ghost' ? '1px solid var(--theme-border, #e5e7eb)' : 'none',
    background: kind === 'primary' ? ACCENT : kind === 'danger' ? '#dc2626' : kind === 'info' ? '#2563eb' : kind === 'teal' ? '#0f766e' : kind === 'ghost' ? 'var(--theme-surface, #fff)' : '#4a5568',
    color: kind === 'ghost' ? 'var(--theme-text, #374151)' : '#fff',
  });
  const tableWrap = { overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10 };
  const table = { width: '100%', minWidth: '760px', borderCollapse: 'separate', borderSpacing: 0, background: 'var(--theme-surface, #fff)' };
  const th = { textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', background: 'var(--theme-surface-2, #f8fafc)', borderBottom: '1px solid var(--theme-border, #e5e7eb)', whiteSpace: 'nowrap', position: 'sticky', top: 0 };
  const td = { textAlign: 'left', padding: '8px 12px', fontSize: 13, borderBottom: '1px solid var(--theme-border-light, #f1f5f9)', color: 'var(--theme-text, #1f2937)' };
  const zebra = (i) => (i % 2 === 1 ? { background: 'var(--theme-surface-2, #f8fafc)' } : null);
  const kpiTile = { flex: '1 1 150px', minWidth: 145, background: 'var(--theme-surface-2, #f8fafc)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: '0.7rem 1rem' };
  const kpiLabel = { fontSize: '0.72rem', color: 'var(--theme-text-muted, #64748b)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' };
  const kpiValue = { fontSize: '1.15rem', fontWeight: 800, marginTop: 2 };
  const pill = (bg, fg) => ({ background: bg, color: fg, borderRadius: 20, padding: '2px 10px', fontSize: 11.5, fontWeight: 700, display: 'inline-block', whiteSpace: 'nowrap' });
  const emptyBox = { padding: '1.4rem', textAlign: 'center', color: 'var(--theme-text-muted, #94a3b8)', background: 'var(--theme-surface-2, #f8fafc)', borderRadius: 10, fontSize: '0.85rem' };

  const num = (v) => Number(v || 0).toFixed(2);
  const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (v) => (v ? fmtDateTime(v) : '-');
  // Meal period = the active Meal Period (KOT & POS → Masters → Meal Period
  // Configuration; KOT Time Slots when none configured) whose From–To window
  // contains the bill's opened time in IST. Periods crossing midnight wrap.
  // No matching period → blank.
  const toMin = (t) => {
    const [h, m] = String(t || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
  };
  const mealPeriod = (v) => {
    if (!v || !timeSlots.length) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
    const mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
    const slot = timeSlots.find((s) => {
      const from = toMin(s.from_time);
      const to = toMin(s.to_time);
      if (Number.isNaN(from) || Number.isNaN(to)) return false;
      return from <= to ? (mins >= from && mins < to) : (mins >= from || mins < to);
    });
    return slot ? String(slot.name || '').trim() : '';
  };
  const MEAL_PILL = { breakfast: ['#fef9c3', '#854d0e'], lunch: ['#dcfce7', '#166534'], dinner: ['#e0e7ff', '#3730a3'] };
  const mealPill = (name) => MEAL_PILL[String(name).toLowerCase()] || ['#f1f5f9', '#475569'];
  const escapeCsv = (value) => {
    const str = String(value ?? '');
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };
  const downloadFile = (content, mimeType, fileName) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const exportCsv = (fileName, columns, dataRows) => {
    const header = columns.map((c) => escapeCsv(c.label)).join(',');
    const body = dataRows.map((r) => columns.map((c) => escapeCsv(c.value(r))).join(',')).join('\n');
    downloadFile(`${header}\n${body}`, 'text/csv;charset=utf-8;', `${fileName}.csv`);
  };
  const escapeHtml = (value) => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const exportExcel = (fileName, columns, dataRows) => {
    const head = `<tr>${columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('')}</tr>`;
    const body = dataRows.map((r) => `<tr>${columns.map((c) => `<td>${escapeHtml(c.value(r))}</td>`).join('')}</tr>`).join('');
    const html = `<html><head><meta charset="utf-8" /></head><body><table border="1">${head}${body}</table></body></html>`;
    downloadFile(html, 'application/vnd.ms-excel;charset=utf-8;', `${fileName}.xls`);
  };
  const exportButtons = (fileName, columns, dataRows) => (
    <div style={{ display: 'flex', gap: '0.4rem' }}>
      <button type="button" onClick={() => exportCsv(fileName, columns, dataRows)} style={{ ...btn('ghost'), padding: '0.32rem 0.6rem', fontSize: '0.76rem' }}>⭳ CSV</button>
      <button type="button" onClick={() => exportExcel(fileName, columns, dataRows)} style={{ ...btn('ghost'), padding: '0.32rem 0.6rem', fontSize: '0.76rem' }}>⭳ Excel</button>
    </div>
  );

  // A tab that just renders {label, columns, rows, rowKey, renderRow} without
  // its own local state — used for the six simple operational report tabs so
  // their markup doesn't repeat the card/table/export boilerplate five times.
  const ReportTable = ({ title, subtitle, columns, exportFileName, exportColumns, dataRows, rowKey, renderCells, emptyLabel }) => (
    <div style={card}>
      <div style={sectionHead}>
        <div>
          <h3 style={sectionTitle}>{title}</h3>
          {subtitle && <p style={sectionSub}>{subtitle}</p>}
        </div>
        {dataRows.length > 0 && exportButtons(exportFileName, exportColumns, dataRows)}
      </div>
      {dataRows.length === 0 ? <div style={emptyBox}>{emptyLabel || 'No rows for this filter window.'}</div> : (
        <div style={tableWrap}>
          <table style={table}>
            <thead><tr>{columns.map((c) => <th key={c} style={th}>{c}</th>)}</tr></thead>
            <tbody>
              {dataRows.map((r, i) => (
                <tr key={rowKey(r, i)} style={zebra(i)}>{renderCells(r).map((cell, ci) => <td key={ci} style={td}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const TABS = [
    { key: 'bills', label: 'Bills & Settlement' },
    { key: 'lifecycle', label: 'Order Lifecycle' },
    { key: 'flow', label: 'KOT Ticket Flow' },
    { key: 'stations', label: 'Station Performance' },
    { key: 'tables', label: 'Table Utilization' },
    { key: 'dayend', label: 'Day-End Settlement' },
    { key: 'btc', label: 'Bill to Company' },
    { key: 'salesmix', label: 'Sales Mix' },
    { key: 'demand', label: 'Item Demand' },
    { key: 'demandforecast', label: 'Demand Forecast' },
    { key: 'cancellations', label: 'Cancellations' },
  ];

  // CSV columns per tab — shared by each tab's own CSV/Excel buttons and the
  // Download button on the filter row (which exports whichever tab is open).
  const TAB_EXPORTS = {
    bills: {
      fileName: 'kot_bill_detail',
      rows: rows,
      columns: [
        { label: 'Bill No', value: (r) => r.bill_no || '' },
        { label: 'Status', value: (r) => r.status || '' },
        { label: 'Settlement Mode', value: (r) => r.settlement_mode || '' },
        { label: 'Room', value: (r) => r.room_number || '' },
        { label: 'Order No', value: (r) => r.order_no || '' },
        { label: 'Order Type', value: (r) => r.order_type || '' },
        { label: 'Table', value: (r) => r.table_no || r.table_id || '' },
        { label: 'Grand Total', value: (r) => num(r.grand_total) },
        { label: 'Paid', value: (r) => num(r.paid_total) },
        { label: 'Due', value: (r) => num(r.due_amount) },
        { label: 'Refund', value: (r) => num(r.refund_amount) },
        { label: 'Opened At', value: (r) => fmtDate(r.opened_at) },
        { label: 'Meal Period', value: (r) => mealPeriod(r.opened_at) },
        { label: 'Settled At', value: (r) => fmtDate(r.settled_at) },
        { label: 'Age Minutes', value: (r) => num(r.age_minutes) },
      ],
    },
    lifecycle: {
      fileName: 'kot_order_lifecycle',
      rows: lifecycleRows,
      columns: [
        { label: 'Order No', value: (r) => r.order_no || '' },
        { label: 'Order Type', value: (r) => r.order_type || '' },
        { label: 'Table', value: (r) => r.table_no || '' },
        { label: 'Status', value: (r) => r.status || '' },
        { label: 'Guest Count', value: (r) => r.guest_count || 0 },
        { label: 'Order Total', value: (r) => num(r.order_total) },
        { label: 'Lifecycle Minutes', value: (r) => num(r.lifecycle_minutes) },
        { label: 'Bill', value: (r) => (r.bill_no ? `${r.bill_no} (${r.bill_status || '-'})` : '-') },
      ],
    },
    flow: {
      fileName: 'kot_ticket_flow',
      rows: kotFlowRows,
      columns: [
        { label: 'KOT No', value: (r) => r.kot_no || '' },
        { label: 'Order Id', value: (r) => r.order_id || '' },
        { label: 'Station', value: (r) => r.station_name || r.station_id || '' },
        { label: 'Status', value: (r) => r.status || '' },
        { label: 'Item Qty', value: (r) => r.item_qty || 0 },
        { label: 'Prep Minutes', value: (r) => num(r.prep_minutes) },
        { label: 'Total Minutes', value: (r) => num(r.total_minutes) },
        { label: 'Raised At', value: (r) => fmtDate(r.raised_at) },
      ],
    },
    stations: {
      fileName: 'kot_station_performance',
      rows: stationPerfRows,
      columns: [
        { label: 'Station', value: (r) => r.station_name || '' },
        { label: 'Total KOT', value: (r) => r.total_kots || 0 },
        { label: 'Raised', value: (r) => r.raised_count || 0 },
        { label: 'Preparing', value: (r) => r.preparing_count || 0 },
        { label: 'Ready', value: (r) => r.ready_count || 0 },
        { label: 'Served', value: (r) => r.served_count || 0 },
        { label: 'Avg Prep Minutes', value: (r) => num(r.avg_prep_minutes) },
        { label: 'Avg Total Minutes', value: (r) => num(r.avg_total_minutes) },
      ],
    },
    tables: {
      fileName: 'kot_table_utilization',
      rows: tableUtilRows,
      columns: [
        { label: 'Table', value: (r) => r.table_no || '' },
        { label: 'Order Count', value: (r) => r.order_count || 0 },
        { label: 'Guest Count', value: (r) => r.guest_count || 0 },
        { label: 'Order Total', value: (r) => num(r.order_total) },
        { label: 'Active Order Count', value: (r) => r.active_order_count || 0 },
      ],
    },
    salesmix: {
      fileName: 'kot_sales_mix_order_type',
      rows: salesMixRows,
      columns: [
        { label: 'Order Type', value: (r) => r.order_type || '' },
        { label: 'Orders', value: (r) => r.order_count || 0 },
        { label: 'Item Qty', value: (r) => r.item_qty || 0 },
        { label: 'Sales', value: (r) => num(r.order_total) },
      ],
    },
    demand: {
      fileName: 'kot_item_demand_proxy',
      rows: itemDemandRows,
      columns: [
        { label: 'Menu Item', value: (r) => r.menu_item_name || '' },
        { label: 'Orders', value: (r) => r.order_count || 0 },
        { label: 'Qty', value: (r) => r.qty_total || 0 },
        { label: 'Sales', value: (r) => num(r.sales_total) },
      ],
    },
    demandforecast: {
      fileName: 'kot_item_demand_forecast',
      rows: demandForecastRows,
      columns: [
        { label: 'Menu Item', value: (r) => r.item_name || '' },
        { label: 'Category', value: (r) => r.category_name || '' },
        { label: 'Date', value: (r) => r.date || '' },
        { label: 'Day', value: (r) => r.day_of_week || '' },
        { label: 'Predicted Qty', value: (r) => r.predicted_qty || 0 },
        { label: 'Trend', value: (r) => r.trend || '' },
        { label: 'Confidence', value: (r) => r.confidence || '' },
      ],
    },
    cancellations: {
      fileName: 'kot_cancellations_voids',
      rows: cancelRows,
      columns: [
        { label: 'Order No', value: (r) => r.order_no || '' },
        { label: 'Order Type', value: (r) => r.order_type || '' },
        { label: 'Table', value: (r) => r.table_no || '' },
        { label: 'Status', value: (r) => r.status || '' },
        { label: 'Cancelled Item Qty', value: (r) => r.cancelled_item_qty || 0 },
        { label: 'Cancelled Amount', value: (r) => num(r.cancelled_item_amount) },
        { label: 'Opened At', value: (r) => fmtDate(r.opened_at) },
      ],
    },
  };

  const runSearch = async () => { await loadReport(); await loadOperationalReports(); };

  // Download = CSV of the tab that's open, for the current filter window.
  // Day-End has several tables, so it becomes one CSV with a section per table.
  const downloadActiveTab = () => {
    const range = `${filters.date_from || 'all'}_to_${filters.date_to || 'all'}`;
    if (activeTab === 'dayend') {
      if (!settlementSummary) { setError('Nothing to download yet — click Search first.'); return; }
      const s = settlementSummary;
      const t = s.totals || {};
      const section = (title, columns, list) => [
        escapeCsv(title),
        columns.map((c) => escapeCsv(c.label)).join(','),
        ...(list || []).map((r) => columns.map((c) => escapeCsv(c.value(r))).join(',')),
        '',
      ].join('\n');
      const csv = [
        section('Summary', [
          { label: 'Bills Settled', value: () => t.total_bills || 0 },
          { label: 'Sub Total', value: () => num(t.total_subtotal) },
          { label: 'Discount', value: () => num(t.total_discount) },
          { label: 'Tax', value: () => num(t.total_tax) },
          { label: 'Grand Total', value: () => num(t.total_grand) },
          { label: 'NC Bills', value: () => t.nc_count || 0 },
          { label: 'NC Sale Value', value: () => num(t.nc_sale_total) },
          { label: 'NC Cost Value', value: () => num(t.nc_cost_total) },
          { label: 'NC GST on Cost', value: () => num(t.nc_cost_gst_total) },
          { label: 'Refunds', value: () => num(t.total_refund) },
        ], [t]),
        section('Breakdown by Payment Mode', [
          { label: 'Payment Mode', value: (r) => r.payment_mode || '' },
          { label: 'Bills', value: (r) => r.bill_count || 0 },
          { label: 'Amount Collected', value: (r) => num(r.collected) },
        ], s.payment_breakdown),
        section('NC / Complimentary Bills', [
          { label: 'Bill No', value: (r) => r.bill_no || '' },
          { label: 'Date', value: (r) => String(r.business_date || '').slice(0, 10) },
          { label: 'Table', value: (r) => r.table_no || '' },
          { label: 'Order Type', value: (r) => r.order_type || '' },
          { label: 'Bill Amount', value: (r) => num(r.grand_total) },
          { label: 'Total Sale Value', value: (r) => num(r.nc_sale_value) },
          { label: 'NC Cost %', value: (r) => Number(r.nc_cost_percent || 0) },
          { label: 'NC Cost Value', value: (r) => num(r.nc_cost_value) },
          { label: 'GST on Cost', value: (r) => num(r.nc_cost_gst) },
          { label: 'Reason', value: (r) => r.nc_reason || '' },
          { label: 'Authorised By', value: (r) => r.nc_authorised_by || '' },
        ], s.nc_bills),
        section('Discount Audit Trail', [
          { label: 'Bill No', value: (r) => r.bill_no || '' },
          { label: 'Date', value: (r) => String(r.business_date || '').slice(0, 10) },
          { label: 'Table', value: (r) => r.table_no || '' },
          { label: 'Sub Total', value: (r) => num(r.sub_total) },
          { label: 'Discount', value: (r) => num(r.discount_total) },
          { label: 'Grand Total', value: (r) => num(r.grand_total) },
          { label: 'Reason', value: (r) => r.discount_reason || '' },
          { label: 'Applied By', value: (r) => r.discount_by || '' },
        ], s.discount_audit),
        section('Refund Audit Trail', [
          { label: 'Bill No', value: (r) => r.bill_no || '' },
          { label: 'Date', value: (r) => String(r.business_date || '').slice(0, 10) },
          { label: 'Table', value: (r) => r.table_no || '' },
          { label: 'Order Type', value: (r) => r.order_type || '' },
          { label: 'Bill Total', value: (r) => num(r.grand_total) },
          { label: 'Refund Amount', value: (r) => num(r.refund_amount) },
          { label: 'Mode', value: (r) => r.refund_mode || '' },
          { label: 'Reason', value: (r) => r.refund_reason || '' },
          { label: 'Refunded By', value: (r) => r.refunded_by || '' },
          { label: 'Refunded At', value: (r) => (r.refunded_at ? fmtDate(r.refunded_at) : '') },
        ], s.refund_audit),
        section('Payment Mode Corrections', [
          { label: 'Bill No', value: (r) => r.bill_no || '' },
          { label: 'Date', value: (r) => String(r.business_date || '').slice(0, 10) },
          { label: 'Amount', value: (r) => num(r.amount) },
          { label: 'Old Mode', value: (r) => r.old_mode || '' },
          { label: 'New Mode', value: (r) => r.new_mode || '' },
          { label: 'Reference', value: (r) => r.new_reference || '' },
          { label: 'Reason', value: (r) => r.reason || '' },
          { label: 'Changed By', value: (r) => r.changed_by || '' },
          { label: 'Changed At', value: (r) => (r.changed_at ? fmtDate(r.changed_at) : '') },
        ], s.payment_mode_corrections || []),
      ].join('\n');
      downloadFile(csv, 'text/csv;charset=utf-8;', `kot_day_end_settlement_${range}.csv`);
      return;
    }
    const exp = TAB_EXPORTS[activeTab];
    if (!exp || !(exp.rows || []).length) { setError('Nothing to download for this tab and filter window.'); return; }
    exportCsv(`${exp.fileName}_${range}`, exp.columns, exp.rows);
  };

  return (
    <div style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}>
      {/* Correct Mode (Hotel Admin): payment mode and/or customer GST details */}
      {correctingBill && (() => {
        const lines = correctingBill.payment_lines || [];
        const modeAllowed = canCorrectBill(correctingBill);
        const line = lines.find((l) => String(l.id) === String(correctForm.payment_id)) || lines[0] || {};
        const currentMode = String(line.payment_mode || '').toUpperCase();
        const modeChanged = modeAllowed && (correctForm.new_mode !== currentMode || String(correctForm.reference_no || '').trim() !== String(line.reference_no || ''));
        const refLabel = correctForm.new_mode === 'UPI' ? 'UPI Transaction ID' : 'Reference No';
        const refRequired = modeChanged && (correctForm.new_mode === 'CARD' || correctForm.new_mode === 'UPI');
        const g = correctForm.customer_gstin;
        const gValid = GSTIN_RX.test(g);
        const field = { display: 'block', width: '100%', marginTop: '0.3rem', boxSizing: 'border-box', ...input };
        const sectionHead = { fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#1e3a8a', margin: '0.2rem 0 0.5rem' };
        const settleLabel = correctingBill.nc_reason ? 'Complimentary (NC)'
          : String(correctingBill.settlement_mode || '').toUpperCase() === 'PAY_AT_CHECKOUT' ? 'Room charge'
            : lines.some((l) => String(l.payment_mode || '').toUpperCase() === 'BILL_TO_COMPANY') ? 'Bill to Company' : 'Payment';
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div role="dialog" aria-modal="true" aria-labelledby="correct-dlg-title" style={{ background: 'var(--theme-surface, #fff)', borderRadius: 14, padding: '1.5rem', width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
              <h3 id="correct-dlg-title" style={{ marginTop: 0, color: '#1e3a8a' }}>Correct Bill — Bill #{correctingBill.bill_no}</h3>
              <div style={{ marginBottom: '0.8rem', color: 'var(--theme-text-muted, #4a5568)', fontSize: '0.88rem' }}>
                Grand Total: <b style={{ color: 'var(--theme-text, #1f2937)' }}>{inr(correctingBill.grand_total)}</b>. Amount, tax and bill number stay the same.
              </div>

              <div style={sectionHead}>Payment mode</div>
              {modeAllowed ? (
                <>
                  {lines.length > 1 && (
                    <div style={{ marginBottom: '0.6rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}>Payment line to correct</div>
                      {lines.map((l, idx) => (
                        <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.6rem', border: String(l.id) === String(correctForm.payment_id) ? '2px solid #1e3a8a' : '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8, marginBottom: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
                          <input type="radio" name="correct-line" checked={String(l.id) === String(correctForm.payment_id)}
                            onChange={() => setCorrectForm((f) => ({ ...f, payment_id: String(l.id), new_mode: String(l.payment_mode || '').toUpperCase() || f.new_mode, reference_no: String(l.reference_no || '') }))} />
                          Line {idx + 1} · <b>{String(l.payment_mode || '').toUpperCase()}</b> · {inr(l.amount)}{l.reference_no ? ` · Ref ${l.reference_no}` : ''}
                        </label>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
                    <label style={{ display: 'block' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Current mode</span>
                      <input type="text" value={`${currentMode} · ${inr(line.amount)}`} disabled style={{ ...field, background: 'var(--theme-surface-2, #f1f5f9)' }} />
                    </label>
                    <label style={{ display: 'block' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>New mode</span>
                      <select value={correctForm.new_mode} onChange={(e) => onCorrectModeChange(e.target.value)} style={field}>
                        {[...new Set([currentMode, ...CORRECT_MODES].filter(Boolean))].map((m) => {
                          const splitBlocked = m === 'BILL_TO_COMPANY' && m !== currentMode && lines.length !== 1;
                          return (
                            <option key={m} value={m} disabled={splitBlocked}>
                              {m === currentMode ? `${modeLabel(m)} (no change)` : splitBlocked ? `${modeLabel(m)} (single-payment bills only)` : modeLabel(m)}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  </div>
                  {correctForm.new_mode === 'BILL_TO_COMPANY' && currentMode !== 'BILL_TO_COMPANY' ? (() => {
                    const co = (cmCompanies || []).find((c) => String(c.corporate_id) === String(correctForm.btc_corporate_id)) || null;
                    const limit = Number(co?.credit_limit || 0);
                    const owed = Number(co?.credit_used || 0);
                    const over = co && limit > 0 && owed + Number(line.amount || 0) > limit ? owed + Number(line.amount || 0) - limit : 0;
                    const small = { fontSize: '0.8rem', fontWeight: 600 };
                    return (
                      <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '0.7rem', marginBottom: '0.6rem', display: 'grid', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <label style={{ display: 'block', flex: 3, minWidth: 200 }}>
                            <span style={small}>Company *</span>
                            <select value={correctForm.btc_corporate_id} onChange={(e) => pickCmCompany(e.target.value)} style={field}>
                              <option value="">{cmCompanies === null ? 'Loading companies…' : '— Select company —'}</option>
                              {(cmCompanies || []).map((c) => <option key={c.corporate_id} value={c.corporate_id}>{c.corporate_name}{c.corporate_code ? ` (${c.corporate_code})` : ''}</option>)}
                            </select>
                          </label>
                          <button type="button" onClick={() => { setCmNewOpen((v) => !v); setCmNewMsg(''); }} style={{ ...btn('ghost'), padding: '0.45rem 0.7rem' }}>
                            {cmNewOpen ? 'Close' : '+ New company'}
                          </button>
                        </div>
                        {cmNewOpen && (
                          <div style={{ background: 'var(--theme-surface, #fff)', border: '1px solid #c7d2fe', borderRadius: 8, padding: '0.6rem', display: 'grid', gap: '0.4rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.4rem' }}>
                              <label style={{ display: 'block' }}><span style={small}>Company name *</span>
                                <input value={cmNew.corporate_name} onChange={(e) => setCmNew((n) => ({ ...n, corporate_name: e.target.value }))} maxLength={150} style={field} /></label>
                              <label style={{ display: 'block' }}><span style={small}>GSTIN</span>
                                <input value={cmNew.gst_number} maxLength={15} onChange={(e) => setCmNew((n) => ({ ...n, gst_number: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '') }))} style={{ ...field, fontFamily: 'monospace' }} /></label>
                              <label style={{ display: 'block' }}><span style={small}>Billing address</span>
                                <input value={cmNew.billing_address} onChange={(e) => setCmNew((n) => ({ ...n, billing_address: e.target.value }))} style={field} /></label>
                              <label style={{ display: 'block' }}><span style={small}>Credit limit ₹ (optional)</span>
                                <input type="number" min="0" value={cmNew.credit_limit} placeholder="Blank = no limit" onChange={(e) => setCmNew((n) => ({ ...n, credit_limit: e.target.value }))} style={field} /></label>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <button type="button" onClick={saveCmCompany} disabled={cmNewBusy} style={btn('info')}>{cmNewBusy ? 'Saving…' : 'Save company'}</button>
                            </div>
                          </div>
                        )}
                        {cmNewMsg && <div style={{ fontSize: '0.8rem', color: '#b45309' }}>{cmNewMsg}</div>}
                        {co && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.25rem 0.8rem', fontSize: '0.8rem' }}>
                            <div><span style={{ color: '#64748b' }}>GSTIN:</span> <b>{co.gst_number || '—'}</b></div>
                            <div><span style={{ color: '#64748b' }}>Credit limit:</span> <b>{limit > 0 ? inr(limit) : 'No limit'}</b></div>
                            <div><span style={{ color: '#64748b' }}>Outstanding:</span> <b>{inr(owed)}</b></div>
                            {co.billing_address && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#64748b' }}>Address:</span> {co.billing_address}</div>}
                          </div>
                        )}
                        {over > 0 && (
                          <div style={{ fontSize: '0.8rem', background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 6, padding: '0.35rem 0.55rem' }}>
                            This bill takes {co.corporate_name} {inr(over)} over its credit limit of {inr(limit)}. You can still save.
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.5rem' }}>
                          <label style={{ display: 'block' }}><span style={small}>Guest / employee name *</span>
                            <input value={correctForm.btc_guest} maxLength={150} onChange={(e) => setCorrectForm((f) => ({ ...f, btc_guest: e.target.value }))} style={field} /></label>
                          <label style={{ display: 'block' }}><span style={small}>Employee ID / PO / Ref no</span>
                            <input value={correctForm.btc_ref} maxLength={120} placeholder="Optional" onChange={(e) => setCorrectForm((f) => ({ ...f, btc_ref: e.target.value }))} style={field} /></label>
                        </div>
                      </div>
                    );
                  })() : (
                    <label style={{ display: 'block', marginBottom: '0.6rem' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{refLabel}{refRequired ? ' *' : ''}</span>
                      <input type="text" value={correctForm.reference_no} onChange={(e) => setCorrectForm((f) => ({ ...f, reference_no: e.target.value }))} placeholder={refRequired ? 'Required for this mode' : 'Optional'} maxLength={120} style={field} />
                    </label>
                  )}
                  {modeChanged && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: '#78350f', marginBottom: '0.6rem' }}>
                      Day-End Settlement will move {inr(line.amount)} from {modeLabel(currentMode) || 'the old mode'} to {modeLabel(correctForm.new_mode)}.
                      {correctForm.new_mode === 'BILL_TO_COMPANY' && currentMode !== 'BILL_TO_COMPANY' && ' The amount is added to the company\'s dues.'}
                      {currentMode === 'BILL_TO_COMPANY' && ` The amount comes off ${correctingBill.btc_company_name || 'the company'}'s dues.`}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: '0.84rem', color: 'var(--theme-text-muted, #64748b)', marginBottom: '0.6rem' }}>
                  {correctingBill.btc_corporate_id && Number(correctingBill.btc_paid_amount || 0) > 0.004
                    ? `${correctingBill.btc_company_name || 'The company'} has already paid against this bill, so its mode is locked. You can still edit the GST details below.`
                    : `${settleLabel} bill — the payment mode can't be changed here. You can still add the customer's GST details below.`}
                </div>
              )}

              <div style={{ ...sectionHead, borderTop: '1px solid var(--theme-border, #e5e7eb)', paddingTop: '0.7rem' }}>Customer GST details</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
                <label style={{ display: 'block' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>GSTIN</span>
                  <input type="text" value={g} onChange={(e) => onGstinChange(e.target.value)} placeholder="15-digit GSTIN" maxLength={15} style={{ ...field, fontFamily: 'monospace' }} />
                </label>
                <label style={{ display: 'block' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Company name</span>
                  <input type="text" value={correctForm.customer_name_bill} onChange={(e) => setCorrectForm((f) => ({ ...f, customer_name_bill: e.target.value }))} maxLength={200} placeholder="Trade / company name" style={field} />
                </label>
              </div>
              <div style={{ fontSize: '0.78rem', minHeight: '1.1em', margin: '0.25rem 0 0.4rem', color: g.length === 15 && !gValid ? '#b91c1c' : '#047857' }}>
                {g.length === 15 && !gValid ? 'This is not a valid GSTIN' : gValid ? (gstLookupMsg || `Valid GSTIN · state code ${g.slice(0, 2)}`) : ''}
              </div>
              <label style={{ display: 'block', marginBottom: '0.6rem' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Address</span>
                <input type="text" value={correctForm.customer_address} onChange={(e) => setCorrectForm((f) => ({ ...f, customer_address: e.target.value }))} maxLength={500} placeholder="Optional" style={field} />
              </label>

              <label style={{ display: 'block', marginBottom: '0.8rem' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Reason for correction *</span>
                <textarea rows={2} value={correctForm.reason} onChange={(e) => setCorrectForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Guest asked for a GST bill in company name after payment" maxLength={500} style={{ ...field, fontFamily: 'inherit' }} />
              </label>
              <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-muted, #64748b)', marginBottom: '0.8rem' }}>
                Every change is logged with your name, time and reason (see History). After GST details are saved, the bill opens for printing.
              </div>
              {correctError && <div role="alert" style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.6rem' }}>{correctError}</div>}
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setCorrectingBill(null)} disabled={correctLoading} style={btn('ghost')}>Cancel</button>
                <button type="button" onClick={doCorrectMode} disabled={correctLoading} style={btn('info')}>
                  {correctLoading ? 'Saving...' : 'Save correction'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payment mode correction history */}
      {historyBill && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--theme-surface, #fff)', borderRadius: 14, padding: '1.5rem', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <h3 style={{ marginTop: 0, color: '#1e3a8a' }}>Correction History — Bill #{historyBill.bill_no}</h3>
            {historyRows !== null && historyGstRows.length > 0 && (
              <div style={{ marginBottom: '0.8rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>GST details</div>
                {historyGstRows.map((h) => (
                  <div key={`g${h.id}`} style={{ border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 8, fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <b>{h.new_name ? `${h.new_name} · ${h.new_gstin || ''}` : 'GST details removed'}</b>
                      <span style={{ color: 'var(--theme-text-muted, #64748b)' }}>{fmtDate(h.changed_at)}</span>
                    </div>
                    <div style={{ marginTop: 4 }}>Was: {h.old_name ? `${h.old_name} · ${h.old_gstin || ''}` : 'none'} · by <b>{h.changed_by || '-'}</b></div>
                    <div style={{ marginTop: 2, color: 'var(--theme-text-muted, #64748b)' }}>Reason: {h.reason}</div>
                  </div>
                ))}
                {historyRows.length > 0 && <div style={{ fontWeight: 700, fontSize: '0.85rem', margin: '0.6rem 0 6px' }}>Payment mode</div>}
              </div>
            )}
            {historyRows === null ? <div style={emptyBox}>Loading…</div> : historyRows.length === 0 ? (historyGstRows.length ? null : <div style={emptyBox}>No corrections for this bill.</div>) : historyRows.map((h) => (
              <div key={h.id} style={{ border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 8, fontSize: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <b>{h.old_mode || '-'} → {h.new_mode}</b>
                  <span style={{ color: 'var(--theme-text-muted, #64748b)' }}>{fmtDate(h.changed_at)}</span>
                </div>
                <div style={{ marginTop: 4 }}>{inr(h.amount)}{h.new_reference ? ` · Ref ${h.new_reference}` : ''} · by <b>{h.changed_by || '-'}</b></div>
                <div style={{ marginTop: 2, color: 'var(--theme-text-muted, #64748b)' }}>Reason: {h.reason}</div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.6rem' }}>
              <button type="button" onClick={() => setHistoryBill(null)} style={btn('ghost')}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {refundingBill && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--theme-surface, #fff)', borderRadius: 14, padding: '1.5rem', width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Record Refund — Bill #{refundingBill.bill_no}</h3>
            <div style={{ marginBottom: '0.75rem', color: 'var(--theme-text-muted, #4a5568)', fontSize: '0.9rem' }}>
              Grand Total: <b style={{ color: 'var(--theme-text, #1f2937)' }}>{inr(refundingBill.grand_total)}</b>
            </div>
            <label style={{ display: 'block', marginBottom: '0.6rem' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Refund Amount (₹)</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={refundingBill.grand_total}
                value={refundForm.refund_amount}
                onChange={e => setRefundForm(f => ({ ...f, refund_amount: e.target.value }))}
                style={{ display: 'block', width: '100%', marginTop: '0.3rem', boxSizing: 'border-box', ...input }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: '0.6rem' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Refund Mode</span>
              <select
                value={refundForm.refund_mode}
                onChange={e => setRefundForm(f => ({ ...f, refund_mode: e.target.value }))}
                style={{ display: 'block', width: '100%', marginTop: '0.3rem', ...input }}
              >
                {['CASH', 'CARD', 'UPI', 'OTHER'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Reason</span>
              <input
                type="text"
                value={refundForm.refund_reason}
                onChange={e => setRefundForm(f => ({ ...f, refund_reason: e.target.value }))}
                placeholder="e.g. Guest complaint, wrong item"
                style={{ display: 'block', width: '100%', marginTop: '0.3rem', boxSizing: 'border-box', ...input }}
              />
            </label>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setRefundingBill(null)} disabled={refundLoading} style={btn('ghost')}>Cancel</button>
              <button type="button" onClick={doRefund} disabled={refundLoading} style={btn('danger')}>
                {refundLoading ? 'Recording...' : 'Record Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: '0.75rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: 'var(--theme-text, #1f2937)' }}>KOT Billing &amp; Settlement</h2>
          <div style={{ color: 'var(--theme-text-muted, #64748b)', fontSize: 13, marginTop: 2 }}>Open bills, settle bills, and view operational reports for {outlets.find((o) => String(o.id) === outletId)?.name || 'the selected outlet'}.</div>
        </div>
        <button type="button" onClick={async () => { await loadReport(); await loadOperationalReports(); }} style={btn('primary')} disabled={loading}>
          {loading ? 'Refreshing…' : '⟳ Refresh All'}
        </button>
      </div>

      {error ? <div className="error-banner" style={{ marginBottom: '0.8rem' }}>{error} <span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div> : null}
      {toast ? (
        <div style={{ marginBottom: '0.8rem', background: '#dcfce7', color: '#166534', border: '1px solid #86efac', padding: '0.55rem 0.8rem', borderRadius: 8, fontSize: 13.5 }}>
          {toast}
        </div>
      ) : null}

      {/* Outlet picker */}
      <div style={card}>
        <h3 style={sectionTitle}>Outlet</h3>
        <div style={{ marginTop: '0.7rem' }}>
          {!selectedProperty ? (
            <div style={{ color: '#c53030' }}>Select property first from Hotel Config</div>
          ) : outlets.length === 0 ? (
            <div style={{ color: 'var(--theme-text-muted, #4a5568)' }}>No outlets available for selected property</div>
          ) : (
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              {outlets.map((o) => {
                const active = String(scope.outlet_id || '') === String(o.id || '');
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setScope((prev) => ({ ...prev, outlet_id: String(o.id) }))}
                    style={{
                      ...tapMin,
                      borderRadius: 10,
                      border: active ? `2px solid ${ACCENT}` : '1px solid var(--theme-border, #dcdde1)',
                      background: active ? 'rgba(13,143,111,0.08)' : 'var(--theme-surface, #fff)',
                      color: active ? ACCENT : 'var(--theme-text, #2d3748)',
                      cursor: 'pointer',
                      padding: '0.55rem 1rem',
                      fontWeight: 700,
                      fontSize: 13.5,
                    }}
                  >
                    {o.code ? `${o.code} · ${o.name}` : (o.name || o.id)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={card}>
        <h3 style={sectionTitle}>Filters</h3>
        {/* One row: filters, date range, then Search and Download (wraps on narrow screens). */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', marginTop: '0.7rem' }}>
          <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))} style={{ ...input, flex: '1 1 150px' }}>
            <option value="">All Status</option>
            <option value="OPEN">Open</option>
            <option value="SETTLED">Settled</option>
          </select>
          <select value={filters.order_type} onChange={(e) => setFilters((p) => ({ ...p, order_type: e.target.value }))} style={{ ...input, flex: '1 1 150px' }}>
            <option value="">All Order Types</option>
            <option value="DINE_IN">Dine In</option>
            <option value="ROOM_SERVICE">Room Service</option>
            <option value="TAKE_AWAY">Take Away</option>
          </select>
          <input type="date" title="From date" value={filters.date_from} onChange={(e) => setFilters((p) => syncDateRange({ ...p, date_from: e.target.value }, 'date_from', 'date_to', 'date_from'))} style={{ ...input, flex: '1 1 140px' }} />
          <input type="date" title="To date" value={filters.date_to} min={filters.date_from || undefined} onChange={(e) => setFilters((p) => syncDateRange({ ...p, date_to: e.target.value }, 'date_from', 'date_to', 'date_to'))} style={{ ...input, flex: '1 1 140px' }} />
          <input type="number" min="1" max="1000" title="Row limit" value={filters.limit} onChange={(e) => setFilters((p) => ({ ...p, limit: e.target.value }))} style={{ ...input, flex: '0 1 90px', minWidth: 70 }} placeholder="Row limit" />
          <button type="button" onClick={runSearch} disabled={loading} style={{ ...btn('primary'), flex: '0 0 auto' }}>
            {loading ? 'Searching…' : '🔍 Search'}
          </button>
          <button type="button" onClick={downloadActiveTab} disabled={loading} style={{ ...btn('ghost'), flex: '0 0 auto' }} title="Download the open tab as CSV">
            ⭳ Download
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div role="tablist" aria-label="KOT report views" style={{ display: 'flex', gap: 6, margin: '0 0 1rem', borderBottom: '1px solid var(--theme-border, #e5e7eb)', paddingBottom: 8, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={activeTab === t.key} onClick={() => setActiveTab(t.key)} style={btn(activeTab === t.key ? 'primary' : 'ghost')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Bills & Settlement ── */}
      {activeTab === 'bills' && (
        <>
          <div style={card}>
            <h3 style={sectionTitle}>Settlement Defaults</h3>
            <p style={sectionSub}>Used by the "Settle" action on the Bill Detail table below.</p>
            <div style={{ display: 'grid', gap: '0.6rem', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', marginTop: '0.7rem' }}>
              <select
                value={settlement.settlement_mode}
                onChange={(e) => {
                  const val = e.target.value;
                  setSettlement((p) => ({ ...p, settlement_mode: val, payment_mode: 'CASH' }));
                  setFilters((p) => ({ ...p, settlement_mode: val, payment_mode: '' }));
                }}
                style={input}
              >
                <option value="DIRECT_PAYMENT">Direct Payment</option>
                <option value="PAY_AT_CHECKOUT">Pay at Checkout (Room Charge)</option>
              </select>
              {settlement.settlement_mode === 'DIRECT_PAYMENT' ? (
                <>
                  <select
                    value={settlement.payment_mode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSettlement((p) => ({ ...p, payment_mode: val }));
                      setFilters((p) => ({ ...p, payment_mode: val }));
                    }}
                    style={input}
                  >
                    <option value="CASH">Cash</option>
                    <option value="CARD">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                  </select>
                  <input
                    type="text"
                    value={settlement.reference_no}
                    onChange={(e) => setSettlement((p) => ({ ...p, reference_no: e.target.value }))}
                    style={input}
                    placeholder="Reference No (optional)"
                  />
                </>
              ) : (
                <>
                  <input type="text" value={settlement.room_number} onChange={(e) => setSettlement((p) => ({ ...p, room_number: e.target.value }))} style={input} placeholder="Room Number" />
                  <input type="text" value={settlement.guest_name} onChange={(e) => setSettlement((p) => ({ ...p, guest_name: e.target.value }))} style={input} placeholder="Guest Name" />
                  <input type="text" value={settlement.mobile_last4} onChange={(e) => setSettlement((p) => ({ ...p, mobile_last4: e.target.value.replace(/\D/g, '').slice(-4) }))} style={input} placeholder="Mobile Last 4" />
                  <button type="button" onClick={lookupInhouseGuest} style={btn('info')}>Lookup Room</button>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.85rem' }}>
                    <input type="checkbox" checked={settlement.manager_approved === true} onChange={(e) => setSettlement((p) => ({ ...p, manager_approved: e.target.checked }))} />
                    Manager approved (required above threshold)
                  </label>
                </>
              )}
            </div>
          </div>

          <div style={card}>
            <h3 style={sectionTitle}>Summary</h3>
            <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginTop: '0.7rem' }}>
              <div style={kpiTile}><div style={kpiLabel}>Total Bills</div><div style={kpiValue}>{summary?.total_bills || 0}</div></div>
              <div style={kpiTile}><div style={kpiLabel}>Total Amount</div><div style={kpiValue}>{inr(summary?.total_grand_total)}</div></div>
              <div style={kpiTile}><div style={kpiLabel}>Open Bills</div><div style={{ ...kpiValue, color: '#2563eb' }}>{summary?.open_bill_count || 0}</div></div>
              <div style={kpiTile}><div style={kpiLabel}>Open Total</div><div style={kpiValue}>{inr(summary?.open_grand_total)}</div></div>
              <div style={kpiTile}><div style={kpiLabel}>Settled Bills</div><div style={{ ...kpiValue, color: '#16a34a' }}>{summary?.settled_bill_count || 0}</div></div>
              <div style={kpiTile}><div style={kpiLabel}>Settled Total</div><div style={kpiValue}>{inr(summary?.settled_grand_total)}</div></div>
            </div>
            <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
              <div style={kpiTile}><div style={kpiLabel}>Aging 0–15m</div><div style={kpiValue}>{summary?.aging_0_15_count || 0}</div></div>
              <div style={kpiTile}><div style={kpiLabel}>Aging 16–30m</div><div style={kpiValue}>{summary?.aging_16_30_count || 0}</div></div>
              <div style={kpiTile}><div style={kpiLabel}>Aging 31–60m</div><div style={{ ...kpiValue, color: '#d97706' }}>{summary?.aging_31_60_count || 0}</div></div>
              <div style={kpiTile}><div style={kpiLabel}>Aging 60m+</div><div style={{ ...kpiValue, color: '#dc2626' }}>{summary?.aging_60_plus_count || 0}</div></div>
            </div>
          </div>

          <div style={card}>
            <div style={sectionHead}>
              <h3 style={sectionTitle}>Bill Detail</h3>
              <button type="button" onClick={async () => { await loadReport(); await loadOperationalReports(); }} style={btn('ghost')} disabled={loading}>
                {loading ? 'Refreshing…' : '⟳ Refresh'}
              </button>
            </div>
            {rows.length === 0 ? <div style={emptyBox}>No bill rows found for this filter window.</div> : (
              <div style={tableWrap}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>Bill No</th>
                      <th style={th}>Status</th>
                      <th style={th}>Order No</th>
                      <th style={th}>Order Type</th>
                      <th style={th}>Table</th>
                      <th style={th}>Grand Total</th>
                      <th style={th}>Paid</th>
                      <th style={th}>Pay Mode</th>
                      <th style={th}>Due</th>
                      <th style={th}>Refund</th>
                      <th style={th}>Opened At</th>
                      <th style={th}>Meal Period</th>
                      <th style={th}>Settled At</th>
                      <th style={th}>Age (min)</th>
                      <th style={th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.id} style={zebra(i)}>
                        <td style={{ ...td, fontWeight: 700 }}>{r.bill_no || '-'}</td>
                        <td style={td}>
                          {r.settlement_mode === 'ROOM_FOLIO_AUTO'
                            ? (r.room_checked_out
                                ? <span style={pill('#dcfce7', '#166534')}>Settled (Room {r.room_number || ''})</span>
                                : <span style={pill('#ede9fe', '#6d28d9')}>Posted to Room {r.room_number || ''}</span>)
                            : r.settlement_mode === 'PAY_AT_CHECKOUT'
                            ? (r.room_checked_out
                                ? <span style={pill('#dcfce7', '#166534')}>Settled (Room {r.room_number || ''})</span>
                                : <span style={pill('#dbeafe', '#1e40af')}>Room Charge</span>)
                            : r.status === 'OPEN'
                              ? <span style={pill('#fef3c7', '#92400e')}>Open</span>
                              : <span style={pill('#f1f5f9', '#475569')}>{r.status || '-'}</span>}
                        </td>
                        <td style={td}>{r.order_no ? `#${r.order_no}` : '-'}</td>
                        <td style={td}>{r.order_type || '-'}</td>
                        <td style={td}>{r.table_no || r.table_id || '-'}</td>
                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{inr(r.grand_total)}</td>
                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: 'var(--theme-text-muted)' }}>{inr(r.paid_total)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {(r.payment_lines || []).length
                            ? (r.payment_lines || []).map((l) => String(l.payment_mode || '').toUpperCase()).join(' + ')
                            : '-'}
                          {Number(r.mode_correction_count || 0) > 0 && (
                            <span style={{ ...pill('#dbeafe', '#1e3a8a'), marginLeft: 6 }}>corrected</span>
                          )}
                        </td>
                        <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: Number(r.due_amount) > 0 ? '#d97706' : 'var(--theme-text-muted)', fontWeight: Number(r.due_amount) > 0 ? 700 : 400 }}>{inr(r.due_amount)}</td>
                        <td style={td}>
                          {Number(r.refund_amount || 0) > 0
                            ? <span title={`${r.refund_mode} — ${r.refund_reason}`} style={{ color: '#dc2626', fontWeight: 700 }}>{inr(r.refund_amount)}</span>
                            : '-'}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(r.opened_at)}</td>
                        <td style={td}>
                          {(() => {
                            const mp = mealPeriod(r.opened_at);
                            return mp ? <span style={pill(...mealPill(mp))}>{mp}</span> : '-';
                          })()}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(r.settled_at)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{num(r.age_minutes)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            onClick={async () => { try { await viewKotBillById(r.id, headers); } catch (err) { setError(err?.message || 'Failed to load bill'); } }}
                            style={{ ...btn('teal'), padding: '0.35rem 0.6rem', fontSize: '0.78rem', marginRight: '0.35rem' }}
                          >
                            View Bill
                          </button>
                          {r.status === 'OPEN' ? (
                            <button
                              type="button"
                              onClick={() => settleBill(r)}
                              disabled={settlingId === String(r.id)}
                              style={{ ...btn('info'), padding: '0.35rem 0.6rem', fontSize: '0.78rem' }}
                            >
                              {settlingId === String(r.id) ? 'Settling...' : 'Settle'}
                            </button>
                          ) : r.status === 'SETTLED' && Number(r.refund_amount || 0) === 0 ? (
                            <button
                              type="button"
                              onClick={() => { setRefundingBill(r); setRefundForm({ refund_amount: '', refund_mode: 'CASH', refund_reason: '' }); }}
                              style={{ ...btn('danger'), padding: '0.35rem 0.6rem', fontSize: '0.78rem' }}
                            >
                              Refund
                            </button>
                          ) : null}
                          {canOpenCorrect(r) && (
                            <button
                              type="button"
                              onClick={() => openCorrectMode(r)}
                              title={r.customer_gstin ? `GST bill to ${r.customer_name_bill || ''} (${r.customer_gstin})` : 'Correct the payment mode or add company GST details'}
                              style={{ ...btn('info'), padding: '0.35rem 0.6rem', fontSize: '0.78rem', marginLeft: '0.35rem' }}
                            >
                              Correct Mode
                            </button>
                          )}
                          {(Number(r.mode_correction_count || 0) > 0 || Number(r.gst_change_count || 0) > 0) && (
                            <button
                              type="button"
                              onClick={() => openHistory(r)}
                              style={{ ...btn('ghost'), padding: '0.35rem 0.6rem', fontSize: '0.78rem', marginLeft: '0.35rem' }}
                            >
                              History
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Order Lifecycle ── */}
      {activeTab === 'lifecycle' && (
        <ReportTable
          title="Order Lifecycle"
          subtitle="Every order in this window, from open to bill settlement."
          columns={['Order', 'Type', 'Table', 'Status', 'Guests', 'Total', 'Lifecycle (min)', 'Bill']}
          exportFileName={TAB_EXPORTS.lifecycle.fileName}
          exportColumns={TAB_EXPORTS.lifecycle.columns}
          dataRows={lifecycleRows.slice(0, 200)}
          rowKey={(r) => r.id}
          renderCells={(r) => [
            `#${r.order_no}`, r.order_type || '-', r.table_no || '-', r.status || '-',
            r.guest_count || 0, inr(r.order_total), num(r.lifecycle_minutes),
            r.bill_no ? `${r.bill_no} (${r.bill_status || '-'})` : '-',
          ]}
        />
      )}

      {/* ── KOT Ticket Flow ── */}
      {activeTab === 'flow' && (
        <ReportTable
          title="KOT Ticket Flow"
          subtitle="Kitchen ticket timing per station, from raised to served."
          columns={['KOT', 'Order', 'Station', 'Status', 'Items', 'Prep (min)', 'Total (min)', 'Raised']}
          exportFileName={TAB_EXPORTS.flow.fileName}
          exportColumns={TAB_EXPORTS.flow.columns}
          dataRows={kotFlowRows.slice(0, 200)}
          rowKey={(r) => r.id}
          renderCells={(r) => [r.kot_no, r.order_id, r.station_name || r.station_id, r.status, r.item_qty || 0, num(r.prep_minutes), num(r.total_minutes), fmtDate(r.raised_at)]}
        />
      )}

      {/* ── Station Performance ── */}
      {activeTab === 'stations' && (
        <ReportTable
          title="Station Performance"
          subtitle="Kitchen station throughput and average timing for this window."
          columns={['Station', 'Total KOT', 'Raised', 'Preparing', 'Ready', 'Served', 'Avg Prep (min)', 'Avg Total (min)']}
          exportFileName={TAB_EXPORTS.stations.fileName}
          exportColumns={TAB_EXPORTS.stations.columns}
          dataRows={stationPerfRows}
          rowKey={(r) => r.station_id}
          renderCells={(r) => [r.station_name, r.total_kots || 0, r.raised_count || 0, r.preparing_count || 0, r.ready_count || 0, r.served_count || 0, num(r.avg_prep_minutes), num(r.avg_total_minutes)]}
        />
      )}

      {/* ── Table Utilization ── */}
      {activeTab === 'tables' && (
        <ReportTable
          title="Table Utilization"
          subtitle="Orders, guests, and sales per dine-in table."
          columns={['Table', 'Orders', 'Guests', 'Sales', 'Active Orders']}
          exportFileName={TAB_EXPORTS.tables.fileName}
          exportColumns={TAB_EXPORTS.tables.columns}
          dataRows={tableUtilRows}
          rowKey={(r) => r.table_id}
          renderCells={(r) => [r.table_no || '-', r.order_count || 0, r.guest_count || 0, inr(r.order_total), r.active_order_count || 0]}
        />
      )}

      {/* ── Day-End / Shift Settlement Report ── */}
      {activeTab === 'dayend' && (
        <div style={card}>
          <h3 style={sectionTitle}>Day-End Settlement Report</h3>
          {settlementSummary ? (() => {
            const t = settlementSummary.totals || {};
            const breakdown = settlementSummary.payment_breakdown || [];
            const ncBills = settlementSummary.nc_bills || [];
            const discounts = settlementSummary.discount_audit || [];
            const refunds = settlementSummary.refund_audit || [];
            const corrections = settlementSummary.payment_mode_corrections || [];
            const modeLabel = { CASH: 'Cash', CARD: 'Card', UPI: 'UPI', ROOM_POST: 'Room Posting', UNKNOWN: 'Other' };
            // Header alignment follows the column's values: amounts right, counts centre.
            const DAYEND_RIGHT = ['Amount Collected', 'Amount', 'Sub Total', 'Discount', 'Grand Total', 'Bill Total', 'Refund Amount', 'Total Sale Value', 'NC Cost %', 'NC Cost Value', 'GST on Cost'];
            const ncHasGst = ncBills.some((r) => Number(r.nc_cost_gst || 0) > 0);
            const ncCols = ['Bill #', 'Date', 'Table', 'Order Type', 'Total Sale Value', 'NC Cost %', 'NC Cost Value', ...(ncHasGst ? ['GST on Cost'] : []), 'Reason', 'Authorised By'];
            const dayEndTh = (h) => ({ ...th, textAlign: DAYEND_RIGHT.includes(h) ? 'right' : h === 'Bills' ? 'center' : 'left', whiteSpace: 'nowrap' });
            const modeColor = { CASH: '#16a34a', CARD: '#2563eb', UPI: '#7c3aed', ROOM_POST: '#ea580c', UNKNOWN: '#64748b' };
            return (
              <div style={{ marginTop: '0.7rem' }}>
                <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
                  {[
                    { label: 'Bills Settled', value: t.total_bills || 0, color: 'var(--theme-text, #1f2937)' },
                    { label: 'Sub Total', value: inr(t.total_subtotal), color: 'var(--theme-text, #1f2937)' },
                    { label: 'Discount', value: inr(t.total_discount), color: '#dc2626' },
                    { label: 'Tax', value: inr(t.total_tax), color: '#92400e' },
                    { label: 'Grand Total', value: inr(t.total_grand), color: '#16a34a' },
                    { label: 'NC Bills', value: t.nc_count || 0, color: 'var(--theme-text-muted, #64748b)' },
                    { label: 'NC Sale Value', value: inr(t.nc_sale_total), color: 'var(--theme-text-muted, #64748b)' },
                    { label: 'NC Cost Value', value: inr(t.nc_cost_total), color: '#92400e' },
                    { label: 'Refunds', value: inr(t.total_refund), color: '#dc2626' },
                  ].map(s => (
                    <div key={s.label} style={kpiTile}>
                      <div style={kpiLabel}>{s.label}</div>
                      <div style={{ ...kpiValue, color: s.color }}>{s.value}</div>
                    </div>
                  ))}
                </div>

                <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: 'var(--theme-text, #1f2937)' }}>Breakdown by Payment Mode</h4>
                {breakdown.length === 0 ? <div style={emptyBox}>No settled payments for this period.</div> : (
                  <div style={{ ...tableWrap, marginBottom: '1.2rem' }}>
                    <table style={table}>
                      <thead><tr>{['Payment Mode', 'Bills', 'Amount Collected'].map(h => <th key={h} style={dayEndTh(h)}>{h}</th>)}</tr></thead>
                      <tbody>
                        {breakdown.map((r, i) => (
                          <tr key={i} style={zebra(i)}>
                            <td style={td}><span style={pill(modeColor[r.payment_mode] || '#64748b', '#fff')}>{modeLabel[r.payment_mode] || r.payment_mode}</span></td>
                            <td style={{ ...td, textAlign: 'center' }}>{r.bill_count}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{inr(r.collected)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h4 style={{ margin: '0.9rem 0 0.5rem', fontSize: '0.85rem', color: 'var(--theme-text, #1f2937)' }}>NC / Complimentary Bills ({ncBills.length})</h4>
                {ncBills.length === 0 ? <div style={emptyBox}>No NC bills for this period.</div> : (
                  <div style={{ ...tableWrap, marginBottom: '1.2rem' }}>
                    <table style={table}>
                      <thead><tr>{ncCols.map(h => <th key={h} style={dayEndTh(h)}>{h}</th>)}</tr></thead>
                      <tbody>
                        {ncBills.map((r, i) => (
                          <tr key={i} style={zebra(i)}>
                            <td style={td}>{r.bill_no || '-'}</td>
                            <td style={td}>{String(r.business_date || '').slice(0, 10)}</td>
                            <td style={td}>{r.table_no || '-'}</td>
                            <td style={td}>{r.order_type || '-'}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{inr(r.nc_sale_value)}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{Number(r.nc_cost_percent || 0) > 0 ? `${Number(r.nc_cost_percent)}%` : '-'}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#92400e', fontWeight: 700 }}>{inr(r.nc_cost_value)}</td>
                            {ncHasGst && <td style={{ ...td, textAlign: 'right' }}>{inr(r.nc_cost_gst)}</td>}
                            <td style={td}>{r.nc_reason || '-'}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{r.nc_authorised_by || '-'}</td>
                          </tr>
                        ))}
                        <tr style={{ background: '#f8fafc' }}>
                          <td style={{ ...td, fontWeight: 800 }} colSpan={4}>Total</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{inr(t.nc_sale_total)}</td>
                          <td style={td} />
                          <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{inr(t.nc_cost_total)}</td>
                          {ncHasGst && <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{inr(t.nc_cost_gst_total)}</td>}
                          <td style={td} colSpan={2} />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                <h4 style={{ margin: '0.9rem 0 0.5rem', fontSize: '0.85rem', color: 'var(--theme-text, #1f2937)' }}>Discount Audit Trail ({discounts.length})</h4>
                {discounts.length === 0 ? <div style={emptyBox}>No discounts applied for this period.</div> : (
                  <div style={tableWrap}>
                    <table style={table}>
                      <thead><tr>{['Bill #', 'Date', 'Table', 'Sub Total', 'Discount', 'Grand Total', 'Reason', 'Applied By'].map(h => <th key={h} style={dayEndTh(h)}>{h}</th>)}</tr></thead>
                      <tbody>
                        {discounts.map((r, i) => (
                          <tr key={i} style={zebra(i)}>
                            <td style={td}>{r.bill_no || '-'}</td>
                            <td style={td}>{String(r.business_date || '').slice(0, 10)}</td>
                            <td style={td}>{r.table_no || '-'}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{inr(r.sub_total)}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>-{inr(r.discount_total)}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{inr(r.grand_total)}</td>
                            <td style={td}>{r.discount_reason || '-'}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{r.discount_by || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h4 style={{ margin: '0.9rem 0 0.5rem', fontSize: '0.85rem', color: '#dc2626' }}>Refund Audit Trail ({refunds.length})</h4>
                {refunds.length === 0 ? <div style={emptyBox}>No refunds recorded for this period.</div> : (
                  <div style={tableWrap}>
                    <table style={table}>
                      <thead><tr>{['Bill #', 'Date', 'Table', 'Order Type', 'Bill Total', 'Refund Amount', 'Mode', 'Reason', 'Refunded By', 'Refunded At'].map(h => <th key={h} style={dayEndTh(h)}>{h}</th>)}</tr></thead>
                      <tbody>
                        {refunds.map((r, i) => (
                          <tr key={i} style={zebra(i)}>
                            <td style={td}>{r.bill_no || '-'}</td>
                            <td style={td}>{String(r.business_date || '').slice(0, 10)}</td>
                            <td style={td}>{r.table_no || '-'}</td>
                            <td style={td}>{r.order_type || '-'}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{inr(r.grand_total)}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{inr(r.refund_amount)}</td>
                            <td style={td}>{r.refund_mode || '-'}</td>
                            <td style={td}>{r.refund_reason || '-'}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{r.refunded_by || '-'}</td>
                            <td style={td}>{r.refunded_at ? fmtDate(r.refunded_at) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h4 style={{ margin: '0.9rem 0 0.5rem', fontSize: '0.85rem', color: '#1e3a8a' }}>Payment Mode Corrections ({corrections.length})</h4>
                {corrections.length === 0 ? <div style={emptyBox}>No payment mode corrections for this period.</div> : (
                  <div style={tableWrap}>
                    <table style={table}>
                      <thead><tr>{['Bill #', 'Date', 'Amount', 'Old Mode', 'New Mode', 'Reference', 'Reason', 'Changed By', 'Changed At'].map(h => <th key={h} style={dayEndTh(h)}>{h}</th>)}</tr></thead>
                      <tbody>
                        {corrections.map((r, i) => (
                          <tr key={r.id} style={zebra(i)}>
                            <td style={td}>{r.bill_no || '-'}</td>
                            <td style={td}>{String(r.business_date || '').slice(0, 10)}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{inr(r.amount)}</td>
                            <td style={td}>{r.old_mode || '-'}</td>
                            <td style={{ ...td, fontWeight: 700 }}>{r.new_mode}</td>
                            <td style={td}>{r.new_reference || '-'}</td>
                            <td style={td}>{r.reason || '-'}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{r.changed_by || '-'}</td>
                            <td style={td}>{r.changed_at ? fmtDate(r.changed_at) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })() : <div style={emptyBox}>Run the report to see the day-end settlement summary.</div>}
        </div>
      )}

      {/* ── Sales Mix ── */}
      {activeTab === 'salesmix' && (
        <ReportTable
          title="Sales Mix by Order Type"
          subtitle="Revenue split across Dine In / Room Service / Take Away."
          columns={['Order Type', 'Orders', 'Item Qty', 'Sales']}
          exportFileName={TAB_EXPORTS.salesmix.fileName}
          exportColumns={TAB_EXPORTS.salesmix.columns}
          dataRows={salesMixRows}
          rowKey={(r) => r.order_type}
          renderCells={(r) => [r.order_type, r.order_count || 0, r.item_qty || 0, inr(r.order_total)]}
        />
      )}

      {/* ── Item Demand ── */}
      {activeTab === 'demand' && (
        <ReportTable
          title="Item Demand"
          subtitle="A proxy for inventory planning — most-ordered menu items in this window."
          columns={['Menu Item', 'Orders', 'Qty', 'Sales']}
          exportFileName={TAB_EXPORTS.demand.fileName}
          exportColumns={TAB_EXPORTS.demand.columns}
          dataRows={itemDemandRows}
          rowKey={(r) => r.menu_item_id}
          renderCells={(r) => [r.menu_item_name, r.order_count || 0, r.qty_total || 0, inr(r.sales_total)]}
        />
      )}

      {/* ── Item Demand Forecast ── */}
      {activeTab === 'demandforecast' && (
        <ReportTable
          title="Item Demand Forecast — Next 7 Days"
          subtitle="Day-of-week baseline + recent-trend adjustment per menu item. Deterministic (no AI) — same approach as the Banquet Demand Forecast."
          columns={['Menu Item', 'Category', 'Date', 'Day', 'Predicted Qty', 'Trend', 'Confidence']}
          exportFileName={TAB_EXPORTS.demandforecast.fileName}
          exportColumns={TAB_EXPORTS.demandforecast.columns}
          dataRows={demandForecastRows}
          rowKey={(r) => `${r.menu_item_id}-${r.date}`}
          renderCells={(r) => [
            r.item_name,
            r.category_name,
            r.date,
            r.day_of_week,
            r.predicted_qty || 0,
            r.trend === 'UP' ? '▲ Up' : r.trend === 'DOWN' ? '▼ Down' : '– Flat',
            r.confidence,
          ]}
        />
      )}

      {/* ── Bill to Company ── */}
      {activeTab === 'btc' && (
        <KotBillToCompany headers={headers} isMobile={isMobile} />
      )}

      {/* ── Cancellations ── */}
      {activeTab === 'cancellations' && (
        <ReportTable
          title="Cancellations / Voids"
          subtitle="Items cancelled or voided after being raised to the kitchen."
          columns={['Order', 'Type', 'Table', 'Status', 'Cancelled Qty', 'Cancelled Amount', 'Opened']}
          exportFileName={TAB_EXPORTS.cancellations.fileName}
          exportColumns={TAB_EXPORTS.cancellations.columns}
          dataRows={cancelRows}
          rowKey={(r) => r.id}
          emptyLabel="No cancellation rows for this filter window."
          renderCells={(r) => [`#${r.order_no}`, r.order_type || '-', r.table_no || '-', r.status || '-', r.cancelled_item_qty || 0, inr(r.cancelled_item_amount), fmtDate(r.opened_at)]}
        />
      )}
    </div>
  );
}

export default KotBillReports;
